import React, { useState, useMemo } from "react";
import { Search, AlertCircle, Eye } from "lucide-react";

export default function PerformanceMissingDataTable({ missingData = [], collaborateurs = [], onViewDetail }) {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    // Build a map of progressId -> collaborator info for fast lookups
    const collaboratorMap = useMemo(() => {
        const map = {};
        collaborateurs.forEach(c => {
            map[c.evaluationProgressId] = {
                nom: c.collaborateurNom,
                manager: c.managerNom,
                job: c.jobName,
                progressId: c.evaluationProgressId
            };
        });
        return map;
    }, [collaborateurs]);

    // Enhance missing data items with collaborator names and managers
    const enhancedMissingData = useMemo(() => {
        return missingData.map((item, idx) => {
            const collabInfo = collaboratorMap[item.evaluationProgressId] || {};
            return {
                ...item,
                uniqueKey: `${item.evaluationProgressId}-${item.code}-${idx}`,
                collaborateurNom: collabInfo.nom || "Inconnu",
                managerNom: collabInfo.manager || "Aucun",
                jobName: collabInfo.job || ""
            };
        });
    }, [missingData, collaboratorMap]);

    // Apply filtering and search
    const filteredList = useMemo(() => {
        return enhancedMissingData.filter(item => 
            item.collaborateurNom.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.managerNom.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.scope.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [enhancedMissingData, searchTerm]);

    // Apply pagination
    const totalItems = filteredList.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const paginatedList = useMemo(() => {
        const start = (safePage - 1) * pageSize;
        return filteredList.slice(start, start + pageSize);
    }, [filteredList, safePage]);

    const getScopeBadgeColor = (scope) => {
        switch (scope?.toUpperCase()) {
            case "GLOBAL": return "perf-badge--red";
            case "OBJECTIFS": return "perf-badge--blue";
            case "COMPETENCES_GENERIQUES": return "perf-badge--green";
            case "COMPETENCES_SPECIFIQUES": return "perf-badge--blue";
            default: return "perf-badge--blue";
        }
    };

    const getCriticiteText = (scope) => {
        switch (scope?.toUpperCase()) {
            case "GLOBAL": return "Élevée";
            case "OBJECTIFS": return "Moyenne";
            default: return "Faible";
        }
    };

    const getCriticiteBadge = (scope) => {
        switch (scope?.toUpperCase()) {
            case "GLOBAL": return <span className="perf-alert-badge" style={{ backgroundColor: "#fee2e2", color: "#991b1b", borderColor: "#fecaca" }}>Bloquant</span>;
            case "OBJECTIFS": return <span className="perf-alert-badge" style={{ backgroundColor: "#f9fefd", color: "#455584", borderColor: "#ccd7f7" }}>Modéré</span>;
            default: return <span className="perf-alert-badge" style={{ backgroundColor: "#f0fdf4", color: "#166534", borderColor: "#bbf7d0" }}>Mineur</span>;
        }
    };

    return (
        <div>
            {/* Search Toolbar */}
            <div className="perf-cons-toolbar">
                <div className="perf-cons-search-container" style={{ flex: "1 1 100%" }}>
                    <Search size={16} className="perf-cons-search-icon" />
                    <input
                        type="text"
                        placeholder="Rechercher par collaborateur, manager, alerte..."
                        className="perf-cons-search-input"
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                </div>
            </div>

            {/* Table */}
            <div className="perf-table-wrap">
                <table className="perf-table">
                    <thead>
                        <tr>
                            <th>Collaborateur</th>
                            <th>Manager</th>
                            <th>Scope / Domaine</th>
                            <th>Message d'alerte</th>
                            <th>Criticité</th>
                            <th style={{ textAlign: "right" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedList.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="perf-state" style={{ textAlign: "center", padding: "30px" }}>
                                    <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", color: "#166534" }}>
                                        <AlertCircle size={18} /> Aucune donnée manquante ou alerte détectée pour cette campagne.
                                    </span>
                                </td>
                            </tr>
                        ) : (
                            paginatedList.map((item) => (
                                <tr key={item.uniqueKey}>
                                    <td style={{ fontWeight: "700", color: "#2f4595" }}>
                                        {item.collaborateurNom}
                                    </td>
                                    <td>{item.managerNom}</td>
                                    <td>
                                        <span className={`perf-badge ${getScopeBadgeColor(item.scope)}`}>
                                            {item.scope}
                                        </span>
                                    </td>
                                    <td style={{ color: "#475569", fontStyle: "italic" }}>{item.message}</td>
                                    <td>{getCriticiteBadge(item.scope)}</td>
                                    <td style={{ textAlign: "right" }}>
                                        <button 
                                            className="perf-btn" 
                                            style={{ padding: "5px 8px" }} 
                                            onClick={() => onViewDetail(item.evaluationProgressId)}
                                            title="Voir détails"
                                        >
                                            <Eye size={13} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="perf-cons-pagination">
                    <button 
                        className="perf-cons-page-btn" 
                        disabled={safePage === 1}
                        onClick={() => setCurrentPage(safePage - 1)}
                    >
                        Précédent
                    </button>
                    <span className="perf-cons-page-info">
                        Page <strong>{safePage}</strong> sur <strong>{totalPages}</strong> ({totalItems} alertes)
                    </span>
                    <button 
                        className="perf-cons-page-btn" 
                        disabled={safePage === totalPages}
                        onClick={() => setCurrentPage(safePage + 1)}
                    >
                        Suivant
                    </button>
                </div>
            )}
        </div>
    );
}
